import { useEffect, useState } from "react"
import { Box, MenuItem, Select, Typography } from "@mui/material"
import TeamPerformanceTable from "../../components/TeamPerformanceTable/TeamPerformanceTable"
import TimePeriodSelector from "../../components/TimePeriodSelector/TimePeriodSelector"
import IndividualPerformance from "../../components/IndividualPerformance/IndividualPerformance"
import {
  useGetIndividualPerformance,
  useGetTeamsPerformance,
} from "../../hooks/Performance"
import { useGetTeams } from "../../hooks/Teams"

import { GroupingOptions } from "../../api/generated"
import { formatDateForBackend } from "../../util/formatDate"
import { useCurrentUser } from "../../hooks/Users"
import { UserRole } from "../../api/generated";

type TimePeriod = "today" | "this-week" | "this-month" | "this-quarter"

function IndividualPerformanceSection() {
  const [grouping, setGrouping] = useState(GroupingOptions.DAY)


  const datasetValues = useGetIndividualPerformance(grouping)

  const uniqueOpcos = [...new Set(datasetValues.data.map((item) => item.opco))]

  const transformedData = datasetValues.data.reduce((acc, item) => {
    const { grouping_field, completed_items, opco } = item

    let entry = acc.find((e) => e.grouping_field === grouping_field)
    if (!entry) {
      entry = { grouping_field, completed_items: 0 }
      uniqueOpcos.forEach((opcoKey) => (entry[opcoKey] = 0))
      acc.push(entry)
    }

    entry[opco] += completed_items

    return acc
  }, [])

  return (
    <IndividualPerformance
      grouping={grouping}
      setGrouping={setGrouping}
      values={transformedData}
      uniqueOpcos={uniqueOpcos}
    />
  )
}

function TeamPerformanceSection() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod | null>("today")
  const [customRange, setCustomRange] = useState<any>([])
  const [team, setTeam] = useState<number>(-1)
  const [filTeams, setFilTeams] = useState([]);
  const currentUser = useCurrentUser();

  function getStartDate() {
    function getDate() {
      const todaysDate = new Date()
      const fullYear = todaysDate.getFullYear()
      const month = todaysDate.getMonth()
      const date = todaysDate.getDate()
      const day = todaysDate.getDay()

      if (timePeriod === "this-week") {
        const daysDiff = day === 0 ? 6 : day - 1
        const startOfWeekDate = date - daysDiff

        return [fullYear, month + 1, Math.max(startOfWeekDate, 1)]
      }

      if (timePeriod === "this-month") {
        return [fullYear, month + 1, 1]
      }

      if (timePeriod === "this-quarter") {
        const quarterStartMonth = Math.floor(month / 3) * 3
        return [fullYear, quarterStartMonth + 1, 1]
      }

      return [fullYear, month + 1, date]
    }

    return getDate()
      .map((item) => item.toString().padStart(2, "0"))
      .join("-")
  }

  function getEndDate() {
    return formatDateForBackend(Date.now())
  }

  const startDate = customRange[0]?.format("YYYY-MM-DD") || getStartDate()
  const endDate = customRange[1]?.format("YYYY-MM-DD") || getEndDate()
  
  const teams = useGetTeams()
  useEffect(() => {
    const [firstTeam] = currentUser?.data?.role?.name?.toLowerCase() === UserRole.TEAM_LEAD ? filTeams : teams.data!;
    if (team === -1 && firstTeam) {
      setTeam(firstTeam.id)
    }
  }, [team, teams.data, filTeams])

  const performance = useGetTeamsPerformance(team, startDate, endDate)

  useEffect(()=>{
    let flTeams=[];
    for (let i = 0; i < teams?.data?.length; i++) {
      for (let j = 0; j < currentUser?.data?.teams?.length; j++) {
        if (teams?.data[i].name === currentUser.data?.teams[j].name) {
          flTeams.push(teams?.data[i]);
        }
      }
    }
    setFilTeams(flTeams);
  },[teams.data, currentUser.data.teams])
  
  return (
    <>
      <Typography
        sx={{
          color: "var(--grey-grey-06)",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "0.25px",
        }}
      >
        Time period:
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: '18px',
        }}
      >
        <TimePeriodSelector
          value={timePeriod}
          onChange={(timePeriod) => {
            setTimePeriod(timePeriod as TimePeriod)
            setCustomRange([])
          }}
          customValue={customRange}
          startDate={startDate}
          endDate={endDate}
          onChangeCustom={(customRange) => {
            setTimePeriod(null)
            setCustomRange(customRange)
          }}
        />

        <Select
          label="Team"
          value={team}
          onChange={({ target: { value } }) => {
            setTeam(value as number)
          }}
        >
          {team === -1 && <MenuItem value={-1} />}
          {
            (currentUser?.data?.role?.name?.toLowerCase() === UserRole.TEAM_LEAD ? filTeams : teams.data!).map((team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem> 
            ))
          }
          
        </Select>
      </Box>
      <TeamPerformanceTable userMetrics={performance.data!} />
    </>
  )
}

export default function Performance({ type }: { type: "individual" | "team" }) {
  return (
    <Box
      sx={{
        backgroundColor: "var(--background-off-white)",
        padding: "32px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {type === "individual" && <IndividualPerformanceSection />}

      {type === "team" && <TeamPerformanceSection />}
    </Box>
  )
}
